import { supabase } from "../integrations/supabase/client.ts";
import { Connection, PublicKey, LAMPORTS_PER_SOL, Transaction } from "@solana/web3.js";
import { AnchorProvider } from "@coral-xyz/anchor";
import { MoonRunClient } from "./anchor/program-client.ts";
import { PROGRAM_IDS, findTokenConfigPDA, findPoolPDA, findGovernancePDA } from "./anchor/idl-types.ts";
import { logger } from "./logger.ts";

// Type alias for TransactionSignature
type TransactionSignature = string;

declare const process: {
  env: {
    VITE_SOLANA_RPC_URL?: string;
  };
};

declare namespace NodeJS {
  interface Timeout {
    ref(): void;
    unref(): void;
  }
}

export interface DataSyncStatus {
  isSynced: boolean;
  lastSyncTime: Date | null;
  pendingOperations: number;
  error: string | null;
}

export interface TransactionStatus {
  signature: string;
  status: "pending" | "confirmed" | "failed";
  error?: string;
  confirmedAt?: Date;
  retryCount: number;
  lastUpdated: Date;
}

export class DataSynchronizer {
  private connection: Connection;
  private moonRunClient: MoonRunClient;
  private syncStatus: DataSyncStatus;
  private transactionStatuses: Map<TransactionSignature, TransactionStatus>;
  private syncInterval: number | null;
  private pendingOperations: Set<string>;

  constructor(connection: Connection, moonRunClient: MoonRunClient) {
    this.connection = connection;
    this.moonRunClient = moonRunClient;
    this.syncStatus = {
      isSynced: false,
      lastSyncTime: null,
      pendingOperations: 0,
      error: null,
    };
    this.transactionStatuses = new Map();
    this.syncInterval = null;
    this.pendingOperations = new Set();
  }

  // Start periodic synchronization
  startSync(intervalMs: number = 30000) {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }

    this.syncInterval = setInterval(() => {
      this.syncAllData().catch((error) => {
        console.error("Error during periodic sync:", error);
        this.updateSyncStatus({
          error: error.message,
        });
      });
    }, intervalMs);

    // Initial sync
    this.syncAllData().catch((error) => {
      console.error("Error during initial sync:", error);
      this.updateSyncStatus({
        error: error.message,
      });
    });
  }

  // Stop synchronization
  stopSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  // Update sync status
  private updateSyncStatus(updates: Partial<DataSyncStatus>) {
    this.syncStatus = { ...this.syncStatus, ...updates };
    this.syncStatus.pendingOperations = this.pendingOperations.size;
  }

  // Get current sync status
  getSyncStatus(): DataSyncStatus {
    return { ...this.syncStatus };
  }

  // Get transaction status
  getTransactionStatus(signature: TransactionSignature): TransactionStatus | undefined {
    return this.transactionStatuses.get(signature);
  }

  // Add transaction to track
  addTransactionToTrack(signature: TransactionSignature) {
    const status: TransactionStatus = {
      signature,
      status: "pending",
      retryCount: 0,
      lastUpdated: new Date(),
    };

    this.transactionStatuses.set(signature, status);
    this.pendingOperations.add(signature);
    this.updateSyncStatus({});
  }

  // Update transaction status
  async updateTransactionStatus(signature: TransactionSignature, status: TransactionStatus["status"], error?: string) {
    const existingStatus = this.transactionStatuses.get(signature);

    if (existingStatus) {
      const updatedStatus: TransactionStatus = {
        ...existingStatus,
        status,
        error: error || existingStatus.error,
        confirmedAt: status === "confirmed" ? new Date() : existingStatus.confirmedAt,
        lastUpdated: new Date(),
      };

      this.transactionStatuses.set(signature, updatedStatus);

      if (status === "confirmed" || status === "failed") {
        this.pendingOperations.delete(signature);
      }

      this.updateSyncStatus({});

      // If failed, attempt retry
      if (status === "failed" && updatedStatus.retryCount < 3) {
        await this.retryFailedTransaction(signature);
      }
    }
  }

  // Retry failed transaction
  private async retryFailedTransaction(signature: TransactionSignature) {
    try {
      const status = this.transactionStatuses.get(signature);
      if (!status) return;

      // Increment retry count
      const updatedStatus = {
        ...status,
        retryCount: status.retryCount + 1,
        lastUpdated: new Date(),
      };

      this.transactionStatuses.set(signature, updatedStatus);
      this.pendingOperations.add(signature);
      this.updateSyncStatus({});

      // Here you would implement the actual retry logic
      // For now, we'll just log it
      console.log(`Retrying transaction ${signature} (attempt ${updatedStatus.retryCount + 1})`);

      // Simulate retry delay
      await new Promise((resolve) => setTimeout(resolve, 5000));

      // Check if transaction was confirmed in the meantime
      const transaction = await this.connection.getTransaction(signature, {
        commitment: "confirmed",
      });

      if (transaction) {
        await this.updateTransactionStatus(signature, "confirmed");
      } else {
        await this.updateTransactionStatus(signature, "failed", "Transaction still not confirmed after retry");
      }
    } catch (error) {
      console.error(`Error retrying transaction ${signature}:`, error);
      await this.updateTransactionStatus(signature, "failed", error instanceof Error ? error.message : "Unknown error");
    }
  }

  // Sync all data between blockchain and database
  async syncAllData() {
    const timerId = logger.startTimer('sync_all_data');

    try {
      this.updateSyncStatus({
        isSynced: false,
        error: null,
      });

      logger.info("Starting data synchronization...");

      // Sync tokens
      await logger.withTimer('sync_tokens', () => this.syncTokens());

      // Sync pools
      await logger.withTimer('sync_pools', () => this.syncPools());

      // Sync governance
      await logger.withTimer('sync_governance', () => this.syncGovernance());

      this.updateSyncStatus({
        isSynced: true,
        lastSyncTime: new Date(),
        error: null,
      });

      logger.endTimer(timerId);
      logger.info("Data synchronization completed successfully");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      logger.endTimer(timerId, { error: errorMessage });
      logger.error("Data synchronization failed", { error: errorMessage });

      this.updateSyncStatus({
        isSynced: false,
        error: errorMessage,
      });
      throw error;
    }
  }

  // Sync tokens between blockchain and database
  private async syncTokens() {
    try {
      console.log("Syncing tokens...");

      // Get all tokens from database
      const { data: dbTokens, error: dbError } = await supabase
        .from("coins")
        .select("*")
        .eq("mint_address", "not.is", null);

      if (dbError) {
        throw new Error(`Failed to fetch tokens from database: ${dbError.message}`);
      }

      if (dbTokens.length === 0) {
        console.log("No tokens to sync");
        return;
      }

      // Collect mint addresses and compute PDAs
      const mintAddresses: PublicKey[] = [];
      const tokenConfigPDAs: PublicKey[] = [];
      const tokenMap = new Map<string, any>();

      for (const token of dbTokens) {
        try {
          const mint = new PublicKey(token.mint_address);
          mintAddresses.push(mint);
          const [pda] = findTokenConfigPDA(mint, new PublicKey(PROGRAM_IDS.TOKEN));
          tokenConfigPDAs.push(pda);
          tokenMap.set(token.mint_address, token);
        } catch (error) {
          console.error(`Invalid mint address ${token.mint_address}:`, error);
          await supabase
            .from("coins")
            .update({
              status: "invalid_mint",
              updated_at: Math.floor(Date.now() / 1000),
            })
            .eq("id", token.id);
        }
      }

      // Batch fetch mint accounts and token config accounts
      const [mintAccounts, tokenConfigAccounts] = await Promise.all([
        this.connection.getMultipleAccountsInfo(mintAddresses),
        this.connection.getMultipleAccountsInfo(tokenConfigPDAs),
      ]);

      // Process each token
      for (let i = 0; i < dbTokens.length; i++) {
        const token = dbTokens[i];
        const mintAccount = mintAccounts[i];
        const tokenConfigAccount = tokenConfigAccounts[i];

        try {
          // Check if token account exists
          if (!mintAccount) {
            throw new Error(`Token account not found for ${token.mint_address}`);
          }

          // Check if token config account exists and decode
          if (!tokenConfigAccount) {
            throw new Error(`Token config account not found for ${token.mint_address}`);
          }

          const tokenConfigData = this.moonRunClient.token.decodeTokenConfigData(tokenConfigAccount.data);

          // Update database with on-chain metadata if different
          const updates: any = {
            status: "verified",
            updated_at: Math.floor(Date.now() / 1000),
          };

          if (token.name !== tokenConfigData.name) updates.name = tokenConfigData.name;
          if (token.symbol !== tokenConfigData.symbol) updates.symbol = tokenConfigData.symbol;
          if (token.uri !== tokenConfigData.uri) updates.uri = tokenConfigData.uri;
          if (token.total_supply !== Number(tokenConfigData.totalSupply)) updates.total_supply = Number(tokenConfigData.totalSupply);
          if (token.decimals !== tokenConfigData.decimals) updates.decimals = tokenConfigData.decimals;

          await supabase
            .from("coins")
            .update(updates)
            .eq("id", token.id);

          console.log(`Token ${token.mint_address} verified on chain`);
        } catch (error) {
          console.error(`Error verifying token ${token.mint_address} on chain:`, error);
          // Mark token as potentially invalid
          await supabase
            .from("coins")
            .update({
              status: "verification_failed",
              updated_at: Math.floor(Date.now() / 1000),
            })
            .eq("id", token.id);
        }
      }

      console.log(`Verified ${dbTokens.length} tokens on chain`);
    } catch (error) {
      console.error("Error syncing tokens:", error);
      throw error;
    }
  }

  // Verify token exists on chain
  private async verifyTokenOnChain(token: any) {
    if (!token.mint_address) {
      throw new Error("Token has no mint address");
    }

    try {
      const mintAddress = new PublicKey(token.mint_address);

      // Check if token account exists
      const tokenAccount = await this.connection.getAccountInfo(mintAddress);
      if (!tokenAccount) {
        throw new Error(`Token account not found for ${token.mint_address}`);
      }

      // Get token config data from our program
      const tokenConfigData = await this.moonRunClient.token.getTokenConfigData(mintAddress);
      if (!tokenConfigData) {
        throw new Error(`Token config not found for ${token.mint_address}`);
      }

      // Update database with on-chain metadata if different
      const updates: any = {
        status: "verified",
        updated_at: Math.floor(Date.now() / 1000),
      };

      if (token.name !== tokenConfigData.name) updates.name = tokenConfigData.name;
      if (token.symbol !== tokenConfigData.symbol) updates.symbol = tokenConfigData.symbol;
      if (token.uri !== tokenConfigData.uri) updates.uri = tokenConfigData.uri;
      if (token.total_supply !== Number(tokenConfigData.totalSupply)) updates.total_supply = Number(tokenConfigData.totalSupply);
      if (token.decimals !== tokenConfigData.decimals) updates.decimals = tokenConfigData.decimals;

      await supabase
        .from("coins")
        .update(updates)
        .eq("id", token.id);

      console.log(`Token ${token.mint_address} verified on chain`);
    } catch (error) {
      console.error(`Token verification failed for ${token.mint_address}:`, error);
      throw error;
    }
  }

  // Sync pools between blockchain and database
  private async syncPools() {
    try {
      console.log("Syncing pools...");

      // Get all tokens with pools from database
      const { data: dbTokens, error: dbError } = await supabase
        .from("coins")
        .select("*")
        .eq("mint_address", "not.is", null);

      if (dbError) {
        throw new Error(`Failed to fetch tokens for pool sync: ${dbError.message}`);
      }

      if (dbTokens.length === 0) {
        console.log("No tokens to sync pools for");
        return;
      }

      // Collect pool PDAs
      const poolPDAs: PublicKey[] = [];
      const tokenMap = new Map<string, any>();

      for (const token of dbTokens) {
        try {
          const mint = new PublicKey(token.mint_address);
          const [pda] = findPoolPDA(mint, new PublicKey(PROGRAM_IDS.BONDING_CURVE));
          poolPDAs.push(pda);
          tokenMap.set(token.mint_address, token);
        } catch (error) {
          console.error(`Invalid mint address ${token.mint_address}:`, error);
        }
      }

      // Batch fetch pool accounts
      const poolAccounts = await this.connection.getMultipleAccountsInfo(poolPDAs);

      // Process each pool
      for (let i = 0; i < dbTokens.length; i++) {
        const token = dbTokens[i];
        const poolAccount = poolAccounts[i];

        try {
          if (!poolAccount) {
            console.warn(`No pool found for token ${token.mint_address}`);
            continue;
          }

          const poolData = this.moonRunClient.bondingCurve.decodePoolAccount(poolAccount.data);

          const updates = {
            price: poolData.solReserve.toNumber() / poolData.tokenReserve.toNumber(),
            market_cap: poolData.marketCap.toNumber() / LAMPORTS_PER_SOL,
            liquidity: poolData.solReserve.toNumber() / LAMPORTS_PER_SOL,
            // Calculate holders from on-chain data or keep database value
            updated_at: Math.floor(Date.now() / 1000),
            status: poolData.isLocked ? "locked" : "active",
          };

          // Only update price_change_24h if we have historical data
          if (token.price && updates.price) {
            (updates as any).price_change_24h = ((updates.price - token.price) / token.price) * 100;
          }

          await supabase
            .from("coins")
            .update(updates)
            .eq("id", token.id);

          console.log(`Synced pool data for token ${token.mint_address}`);
        } catch (error) {
          console.error(`Error syncing pool for token ${token.mint_address}:`, error);
          // Continue with other tokens
        }
      }

      console.log(`Synced pools for ${dbTokens.length} tokens`);
    } catch (error) {
      console.error("Error syncing pools:", error);
      throw error;
    }
  }

  // Sync pool data for a specific token
  private async syncTokenPool(token: any) {
    if (!token.mint_address) {
      console.warn(`Token ${token.id} has no mint address, skipping pool sync`);
      return;
    }

    try {
      const mintAddress = new PublicKey(token.mint_address);

      const poolData = await this.moonRunClient.bondingCurve.getPoolData(mintAddress);
      if (!poolData) {
        console.warn(`No pool found for token ${token.mint_address}`);
        return;
      }

      const updates = {
        price: poolData.solReserve.toNumber() / poolData.tokenReserve.toNumber(),
        market_cap: poolData.marketCap.toNumber() / LAMPORTS_PER_SOL,
        liquidity: poolData.solReserve.toNumber() / LAMPORTS_PER_SOL,
        // Calculate holders from on-chain data or keep database value
        updated_at: Math.floor(Date.now() / 1000),
        status: poolData.isLocked ? "locked" : "active",
      };

      // Only update price_change_24h if we have historical data
      if (token.price && updates.price) {
        const priceChange = ((updates.price - token.price) / token.price) * 100;
        (updates as any).price_change_24h = priceChange;
      }

      await supabase
        .from("coins")
        .update(updates)
        .eq("id", token.id);

      console.log(`Synced pool data for token ${token.mint_address}`);
    } catch (error) {
      console.error(`Error syncing pool for token ${token.mint_address}:`, error);
      throw error;
    }
  }


  // Sync governance data
  private async syncGovernance() {
    try {
      console.log("Syncing governance...");

      // Get all tokens with governance from database
      const { data: dbTokens, error: dbError } = await supabase
        .from("coins")
        .select("*")
        .eq("mint_address", "not.is", null);

      if (dbError) {
        throw new Error(`Failed to fetch tokens for governance sync: ${dbError.message}`);
      }

      if (dbTokens.length === 0) {
        console.log("No tokens to sync governance for");
        return;
      }

      // Collect governance PDAs
      const governancePDAs: PublicKey[] = [];
      const tokenMap = new Map<string, any>();

      for (const token of dbTokens) {
        try {
          const mint = new PublicKey(token.mint_address);
          const [pda] = findGovernancePDA(mint, new PublicKey(PROGRAM_IDS.GOVERNANCE));
          governancePDAs.push(pda);
          tokenMap.set(token.mint_address, token);
        } catch (error) {
          console.error(`Invalid mint address ${token.mint_address}:`, error);
        }
      }

      // Batch fetch governance accounts
      const governanceAccounts = await this.connection.getMultipleAccountsInfo(governancePDAs);

      // Process each governance
      for (let i = 0; i < dbTokens.length; i++) {
        const token = dbTokens[i];
        const governanceAccount = governanceAccounts[i];

        try {
          if (!governanceAccount) {
            console.warn(`No governance found for token ${token.mint_address}`);
            continue;
          }

          const governanceData = this.moonRunClient.governance.decodeGovernanceConfig(governanceAccount.data);

          await supabase.from("coins").update({
            votes_to_lock: governanceData.votesToLock.toNumber(),
            votes_to_unlock: governanceData.votesToUnlock.toNumber(),
            is_locked: !governanceData.isActive, // Or based on pool lock status
            updated_at: Math.floor(Date.now() / 1000),
          }).eq("id", token.id);

          console.log(`Synced governance data for token ${token.mint_address}`);
        } catch (error) {
          console.error(`Error syncing governance for token ${token.mint_address}:`, error);
          // Continue with other tokens
        }
      }

      console.log(`Synced governance for ${dbTokens.length} tokens`);
    } catch (error) {
      console.error("Error syncing governance:", error);
      throw error;
    }
  }

  // Sync governance data for a specific token
  private async syncTokenGovernance(token: any) {
    if (!token.mint_address) {
      console.warn(`Token ${token.id} has no mint address, skipping governance sync`);
      return;
    }

    try {
      const mintAddress = new PublicKey(token.mint_address);

      const governanceData = await this.moonRunClient.governance.getGovernanceConfigData(mintAddress);
      if (!governanceData) {
        console.warn(`No governance found for token ${token.mint_address}`);
        return;
      }

      await supabase.from("coins").update({
        votes_to_lock: governanceData.votesToLock.toNumber(),
        votes_to_unlock: governanceData.votesToUnlock.toNumber(),
        is_locked: !governanceData.isActive, // Or based on pool lock status
        updated_at: Math.floor(Date.now() / 1000),
      }).eq("id", token.id);

      console.log(`Synced governance data for token ${token.mint_address}`);
    } catch (error) {
      console.error(`Error syncing governance for token ${token.mint_address}:`, error);
      throw error;
    }
  }


  // Monitor transaction and update status
  async monitorTransaction(signature: TransactionSignature) {
    try {
      // Add to tracking
      this.addTransactionToTrack(signature);

      // Check transaction status
      const transaction = await this.connection.getTransaction(signature, {
        commitment: "confirmed",
      });

      if (transaction) {
        await this.updateTransactionStatus(signature, "confirmed");
        return true;
      } else {
        // Check if transaction failed
        const transactionStatus = await this.connection.getSignatureStatus(signature);
        if (transactionStatus.value?.err) {
          await this.updateTransactionStatus(signature, "failed", transactionStatus.value.err.toString());
          return false;
        }

        // Still pending
        await this.updateTransactionStatus(signature, "pending");
        return false;
      }
    } catch (error) {
      console.error(`Error monitoring transaction ${signature}:`, error);
      await this.updateTransactionStatus(signature, "failed", error instanceof Error ? error.message : "Unknown error");
      return false;
    }
  }

  // Verify data consistency between blockchain and database
  async verifyDataConsistency() {
    try {
      console.log("Verifying data consistency...");

      // Get all tokens from database
      const { data: dbTokens, error: dbError } = await supabase
        .from("coins")
        .select("*");

      if (dbError) {
        throw new Error(`Failed to fetch tokens for consistency check: ${dbError.message}`);
      }

      let consistencyIssues = 0;

      for (const token of dbTokens) {
        try {
          if (token.mint_address) {
            const isConsistent = await this.verifyTokenConsistency(token);
            if (!isConsistent) {
              consistencyIssues++;
            }
          }
        } catch (error) {
          console.error(`Error verifying consistency for token ${token.id}:`, error);
          consistencyIssues++;
        }
      }

      console.log(`Data consistency check completed. Issues found: ${consistencyIssues}`);

      if (consistencyIssues > 0) {
        this.updateSyncStatus({
          error: `${consistencyIssues} consistency issues found`,
        });
        return false;
      } else {
        this.updateSyncStatus({
          error: null,
        });
        return true;
      }
    } catch (error) {
      console.error("Data consistency verification failed:", error);
      this.updateSyncStatus({
        error: error instanceof Error ? error.message : "Unknown error",
      });
      return false;
    }
  }

  // Verify token consistency
  private async verifyTokenConsistency(token: any) {
    try {
      if (!token.mint_address) {
        console.warn(`Token ${token.id} has no mint address`);
        return false;
      }

      const mintAddress = new PublicKey(token.mint_address);

      // Check token exists on chain
      const tokenAccount = await this.connection.getAccountInfo(mintAddress);
      if (!tokenAccount) {
        console.error(`Token ${token.mint_address} not found on chain`);
        return false;
      }

      // Check pool data consistency
      const poolData = await this.moonRunClient.bondingCurve.getPoolData(mintAddress);
      if (poolData) {
        const price = poolData.solReserve.toNumber() / poolData.tokenReserve.toNumber();
        const marketCap = poolData.marketCap.toNumber() / LAMPORTS_PER_SOL;

        // Compare with database values
        const priceDiff = Math.abs((price - (token.price || 0)) / (token.price || 0.0001));
        const marketCapDiff = Math.abs((marketCap - (token.market_cap || 0)) / (token.market_cap || 1));

        if (priceDiff > 0.05 || marketCapDiff > 0.1) { // 5% price diff or 10% market cap diff
          console.warn(`Token ${token.mint_address} has significant data inconsistency`);
          console.warn(`Price diff: ${priceDiff * 100}%, Market cap diff: ${marketCapDiff * 100}%`);
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error(`Error verifying consistency for token ${token.mint_address}:`, error);
      return false;
    }
  }
}

// Factory function
export function createDataSynchronizer(
  rpcUrl?: string
): DataSynchronizer {
  const connection = new Connection(
    rpcUrl || import.meta.env.VITE_SOLANA_RPC_URL || "https://api.devnet.solana.com",
    "confirmed"
  );
  const wallet = {
    publicKey: new PublicKey("11111111111111111111111111111112"),
    signTransaction: async (tx: Transaction) => tx,
    signAllTransactions: async (txs: Transaction[]) => txs,
  };
  const config = { connection, wallet };
  const moonRunClient = new MoonRunClient(config);
  return new DataSynchronizer(connection, moonRunClient);
}